import { Controller } from "@hotwired/stimulus"

/**
 * Carousel Editor Controller
 * Handles multi-file upload (drag & drop + file picker), settings, and slide reordering.
 */
export default class extends Controller {
  static values = {
    uploadUrl: String,
    settingsUrl: String,
    reorderUrl: String
  }

  static targets = ["fileInput", "dropzone", "slideGrid", "bgImageInput"]

  clickUpload() {
    this.fileInputTarget.click()
  }

  handleFileSelect(event) {
    const files = event.target.files
    if (files.length > 0) this.uploadFiles(files)
  }

  dragOver(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = "copy"
    this.dropzoneTarget.classList.add("border-indigo-500", "bg-indigo-50")
  }

  dragLeave(event) {
    this.dropzoneTarget.classList.remove("border-indigo-500", "bg-indigo-50")
  }

  handleDrop(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.remove("border-indigo-500", "bg-indigo-50")

    const files = event.dataTransfer.files
    if (files.length > 0) this.uploadFiles(files)
  }

  async uploadFiles(files) {
    const formData = new FormData()
    let imageCount = 0

    for (const file of files) {
      if (file.type.startsWith("image/")) {
        formData.append("files[]", file)
        imageCount++
      }
    }

    if (imageCount === 0) {
      alert("No image files found")
      return
    }

    // Show uploading state
    this.dropzoneTarget.innerHTML = `
      <div class="flex items-center justify-center gap-2">
        <svg class="w-5 h-5 animate-spin text-indigo-600" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
        </svg>
        <span class="text-sm text-gray-600">Uploading ${imageCount} image${imageCount > 1 ? 's' : ''}...</span>
      </div>
    `

    try {
      const response = await fetch(this.uploadUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: formData
      })

      if (response.ok) {
        window.location.reload()
      } else {
        const data = await response.json().catch(() => ({}))
        alert(data.error || "Upload failed")
        window.location.reload()
      }
    } catch {
      alert("Upload failed")
      window.location.reload()
    }
  }

  async updateSetting(event) {
    const field = event.target.dataset.field
    const value = event.target.value

    try {
      await fetch(this.settingsUrlValue, {
        method: "PATCH",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ [field]: value })
      })
      // Reload to update duration display
      window.location.reload()
    } catch {
      // Silent fail
    }
  }

  async updateSeconds(event) {
    const field = event.target.dataset.field
    const seconds = parseFloat(event.target.value) || 0
    const frames = Math.round(seconds * 30) // 30fps

    try {
      await fetch(this.settingsUrlValue, {
        method: "PATCH",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ [field]: frames })
      })
      window.location.reload()
    } catch {
      // Silent fail
    }
  }

  async updateCheckbox(event) {
    const field = event.target.dataset.field
    const value = event.target.checked

    try {
      await fetch(this.settingsUrlValue, {
        method: "PATCH",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ [field]: value })
      })
    } catch {
      // Silent fail
    }
  }

  clickBgImage() {
    if (this.hasBgImageInputTarget) this.bgImageInputTarget.click()
  }

  async uploadBgImage(event) {
    const file = event.target.files[0]
    if (!file) return

    const formData = new FormData()
    formData.append("file", file)
    formData.append("asset_type", "background_image")

    try {
      const response = await fetch(this.uploadUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: formData
      })
      if (response.ok) window.location.reload()
    } catch {
      alert("Failed to upload background image")
    }
  }

  async removeBgImage() {
    try {
      await fetch(this.settingsUrlValue, {
        method: "PATCH",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ background_image: "" })
      })
      window.location.reload()
    } catch {
      // Silent fail
    }
  }

  // --- Slide drag & drop reordering ---

  slideDragStart(event) {
    const slide = event.target.closest("[data-slide-id]")
    if (!slide) return
    this.draggedSlide = slide
    slide.style.opacity = "0.4"
    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("text/plain", slide.dataset.slideId)
  }

  slideDragEnd(event) {
    const slide = event.target.closest("[data-slide-id]")
    if (slide) slide.style.opacity = "1"
    this.element.querySelectorAll("[data-slide-id]").forEach(s => {
      s.classList.remove("ring-2", "ring-indigo-500")
    })
    this.draggedSlide = null
  }

  slideDragOver(event) {
    event.preventDefault()
    // Don't trigger the main dropzone
    event.stopPropagation()
    const slide = event.target.closest("[data-slide-id]")
    if (!slide || slide === this.draggedSlide) return

    this.element.querySelectorAll("[data-slide-id]").forEach(s => {
      s.classList.remove("ring-2", "ring-indigo-500")
    })
    slide.classList.add("ring-2", "ring-indigo-500")
  }

  async slideDrop(event) {
    event.preventDefault()
    event.stopPropagation()

    const target = event.target.closest("[data-slide-id]")
    if (!target || !this.draggedSlide || target === this.draggedSlide) return

    // Move in DOM
    const grid = this.slideGridTarget
    const slides = Array.from(grid.querySelectorAll("[data-slide-id]"))
    const dragIdx = slides.indexOf(this.draggedSlide)
    const dropIdx = slides.indexOf(target)

    if (dragIdx < dropIdx) {
      target.parentNode.insertBefore(this.draggedSlide, target.nextSibling)
    } else {
      target.parentNode.insertBefore(this.draggedSlide, target)
    }

    // Clear styles
    grid.querySelectorAll("[data-slide-id]").forEach((s, i) => {
      s.style.opacity = "1"
      s.classList.remove("ring-2", "ring-indigo-500")
      const num = s.querySelector("span")
      if (num) num.textContent = i + 1
    })

    // Persist
    const order = Array.from(grid.querySelectorAll("[data-slide-id]"))
      .map(s => s.dataset.slideId)

    try {
      await fetch(this.reorderUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ order })
      })
    } catch {
      window.location.reload()
    }
  }
}
